/**
 * Invoice PTC comercial en Excel — misma composición que el PDF.
 */
import ExcelJS from 'exceljs';
import ptcImage from '../../imagenes/ptc.jpg';
import firmaPtcImage from '../../imagenes/firma-ptc.png';
import {
  normalizeGeneralMerchandiseDescription,
  stripProductNameIfCoveredByTextoArriba,
  usdAmountInWords,
} from './invoicePtcMerchandise';
import { pairFromTextosAlternativosPtc } from './invoicePtcTextosMercaderia';
import { totalVolumeSummaryText } from './invoiceVolumeUnit';
import { totalInvoiceLabel } from './invoiceCurrencyLabel';
import { invoiceNumberCorrelativo } from './invoiceNumberUtils';
import { textosAlternativosParaPdf } from './invoicePtcMerchandiseTable';
import { invoicePtcPdfFileName } from './generateInvoicePtcPDF';
import { invoiceIssueDateText } from './invoiceIssueDate';
import {
  BORDER,
  fmtDateLongEn,
  fmtNumCL,
  fmtPriceEs,
  safe,
  setColWidths,
  styleRange,
  upper,
  workbookToDownload,
} from '../documentos/commercialExcelUtils';

function excelRowHeightForWrappedText(text, charsPerLine = 48, lineHeight = 12, minHeight = 20) {
  const lines = Math.max(1, Math.ceil(safe(text).length / charsPerLine));
  return Math.max(minHeight, lines * lineHeight);
}

const RUT_EMPRESA = '77.710.540-K';
const SII_OFICINA = 'S.I.I. SANTIAGO ORIENTE';
const COMPANY_LINES = [
  'PALM TRADE CHILE SPA',
  'Avenida Presidente Kennedy 5146, oficina 51',
  'Vitacura, Santiago, Chile',
  'Fono: (56-2) 22190119 - Fax: (56-2) 22184390',
];

const WHITE_FILL = {
  type: 'pattern',
  pattern: 'solid',
  fgColor: { argb: 'FFFFFFFF' },
};

const VERTICAL_BORDER = {
  left: BORDER.left,
  right: BORDER.right,
};

const DOCUMENT_COLUMN_OFFSET = 1;

async function imageUrlToDataUrl(url, mimeType) {
  const response = await fetch(url);
  if (!response.ok) throw new Error('No se pudo cargar una imagen de Invoice PTC.');
  const bytes = new Uint8Array(await response.arrayBuffer());
  let binary = '';
  for (let i = 0; i < bytes.length; i += 1) binary += String.fromCharCode(bytes[i]);
  return `data:${mimeType};base64,${btoa(binary)}`;
}

function setMergedCell(ws, r1, c1, r2, c2, value, options = {}) {
  const shiftedC1 = c1 + DOCUMENT_COLUMN_OFFSET;
  const shiftedC2 = c2 + DOCUMENT_COLUMN_OFFSET;
  if (r1 !== r2 || shiftedC1 !== shiftedC2) ws.mergeCells(r1, shiftedC1, r2, shiftedC2);
  const cell = ws.getCell(r1, shiftedC1);
  cell.value = value || null;
  cell.font = {
    name: 'Arial',
    size: 8,
    color: { argb: 'FF000000' },
    ...(options.font || {}),
  };
  cell.alignment = {
    horizontal: 'center',
    vertical: 'middle',
    wrapText: true,
    ...(options.alignment || {}),
  };
  if (options.border) {
    styleRange(ws, r1, shiftedC1, r2, shiftedC2, { border: options.border });
  }
}

function applyOuterBorder(ws, r1, c1, r2, c2) {
  const shiftedC1 = c1 + DOCUMENT_COLUMN_OFFSET;
  const shiftedC2 = c2 + DOCUMENT_COLUMN_OFFSET;
  for (let row = r1; row <= r2; row += 1) {
    for (let col = shiftedC1; col <= shiftedC2; col += 1) {
      const cell = ws.getCell(row, col);
      cell.border = {
        ...cell.border,
        ...(row === r1 ? { top: BORDER.top } : {}),
        ...(row === r2 ? { bottom: BORDER.bottom } : {}),
        ...(col === shiftedC1 ? { left: BORDER.left } : {}),
        ...(col === shiftedC2 ? { right: BORDER.right } : {}),
      };
    }
  }
}

function writeDocumentRow(ws, rowIndex, values, options = {}) {
  const row = ws.getRow(rowIndex);
  values.forEach((value, index) => {
    const cell = row.getCell(index + 1 + DOCUMENT_COLUMN_OFFSET);
    cell.value = value === '' || value === undefined ? null : value;
    if (options.border) cell.border = BORDER;
    if (options.font) cell.font = { ...cell.font, ...options.font };
    if (options.fill) cell.fill = options.fill;
    if (options.align) cell.alignment = options.align;
    if (options.wrap) {
      cell.alignment = { ...(cell.alignment || {}), wrapText: true, ...options.align };
    }
  });
  if (options.height) row.height = options.height;
  return row;
}

function setGridCells(ws, row, ranges, values, options = {}) {
  ranges.forEach(([from, to], index) => {
    setMergedCell(ws, row, from, row, to, values[index], {
      ...options,
      border: BORDER,
    });
  });
}

function loadingPortForExcel(port) {
  return safe(port)
    .replace(/\s*\((?:BRASIL|BRAZIL)\)\s*/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function unloadingPortForExcel(port, country) {
  const cleanPort = safe(port).replace(/\s*\([^)]*\)\s*/g, ' ').replace(/\s+/g, ' ').trim();
  const cleanCountry = safe(country).replace(/\s+/g, ' ').trim();
  return [cleanPort, cleanCountry].filter(Boolean).join(', ');
}

function totalDueWordsPtc(amount, currency = 'USD') {
  const n = Math.round(Number(amount) * 100) / 100;
  if (!Number.isFinite(n) || n <= 0) return '';
  const whole = Math.floor(n);
  const cents = Math.round((n - whole) * 100);
  const words = usdAmountInWords(whole)
    .replace(/\s+DOLLARS?$/i, '')
    .trim();
  const suffix = String(currency).toUpperCase() === 'EUR' ? 'EUROS' : 'USD DOLLARS';
  return `${words}, ${String(cents).padStart(2, '0')}/100 ${suffix}`;
}

function addressLines(texto, paisDestino) {
  const country = upper(paisDestino);
  return safe(texto)
    .split(/\r?\n/)
    .map((ln) => ln.trim())
    .filter(Boolean)
    .map((ln) => upper(ln))
    .filter((ln) => !country || ln !== country);
}

function invoicePtcExcelFileName(params) {
  return invoicePtcPdfFileName(params).replace(/\.pdf$/i, '.xlsx');
}

function appendBankSheet(wb, logoBase64) {
  const ws = wb.addWorksheet('Información Bancaria', {
    views: [{ showGridLines: false }],
    pageSetup: {
      orientation: 'portrait',
      paperSize: 9,
      fitToPage: true,
      fitToWidth: 1,
      fitToHeight: 1,
      margins: { left: 0.65, right: 0.65, top: 0.7, bottom: 0.7, header: 0, footer: 0 },
    },
  });
  setColWidths(ws, [3, 24, 24, 24, 24, 24, 24, 24, 24]);
  const logoId = wb.addImage({ base64: logoBase64, extension: 'jpeg' });
  ws.addImage(logoId, {
    tl: { col: 1.35, row: 2.1 },
    ext: { width: 245, height: 190 },
    editAs: 'oneCell',
  });

  let r = 13;
  setMergedCell(ws, r, 1, r, 8, 'Información Bancaria', {
    font: { bold: true, size: 9 },
    alignment: { horizontal: 'left' },
  });
  r += 2;
  const rows = [
    ['Beneficiario', 'Palm Trade Chile SPA'],
    ['Identificador Fiscal', 'RUT 77.710.540-K'],
    ['Dirección', 'Avenida Presidente Kennedy 5146 oficina 51 Vitacura, Santiago , Chile'],
    ['', ''],
    ['Banco Beneficiario', 'BCI Miami Branch'],
    ['Dirección', '200 South Biscayne Blvd., Suite 2300 Miami, FL  33131 ,USA'],
    ['ABA :', '66014726'],
    ['Swift:', 'CREDUS3M'],
    ['Cuenta', '120010814'],
  ];
  for (const [label, value] of rows) {
    setMergedCell(ws, r, 1, r, 2, label, { alignment: { horizontal: 'left' } });
    setMergedCell(ws, r, 3, r, 8, value, { alignment: { horizontal: 'left' } });
    ws.getRow(r).height = 15;
    r += 1;
  }
  styleRange(ws, 1, 1, r, 9, {
    fill: WHITE_FILL,
    font: { name: 'Arial', color: { argb: 'FF000000' } },
  });
  ws.pageSetup.printArea = `A1:I${r}`;
}

export async function buildInvoicePtcExcelWorkbook(params) {
  const p = params || {};
  const wb = new ExcelJS.Workbook();
  wb.creator = 'PalmTrade ERP';
  const ws = wb.addWorksheet('Invoice PTC', {
    views: [{ showGridLines: false }],
    pageSetup: {
      orientation: 'portrait',
      paperSize: 9,
      fitToPage: true,
      fitToWidth: 1,
      fitToHeight: 0,
      margins: { left: 0.35, right: 0.35, top: 0.25, bottom: 0.35, header: 0, footer: 0 },
    },
  });
  setColWidths(ws, [3, 14, 14, 40, 6, 9, 9, 12, 12]);

  const [logoBase64, signatureBase64] = await Promise.all([
    imageUrlToDataUrl(ptcImage, 'image/jpeg'),
    imageUrlToDataUrl(firmaPtcImage, 'image/png'),
  ]);
  const logoId = wb.addImage({ base64: logoBase64, extension: 'jpeg' });
  ws.addImage(logoId, {
    tl: { col: 1.15, row: 0.05 },
    ext: { width: 128, height: 100 },
    editAs: 'oneCell',
  });

  [20, 18, 16, 16, 20].forEach((height, index) => {
    ws.getRow(index + 1).height = height;
  });
  setMergedCell(ws, 2, 6, 2, 8, `R.U.T: ${RUT_EMPRESA}`, {
    font: { bold: true, size: 11 },
  });
  setMergedCell(ws, 3, 6, 4, 8, 'FACTURA DE VENTA DE MERCADERIAS\nSITUADAS EN EL EXTRANJERO', {
    font: { size: 8 },
  });
  setMergedCell(ws, 5, 6, 5, 8, `INVOICE N° ${invoiceNumberCorrelativo(p.numeroInvoice) || ''}`, {
    font: { bold: true, size: 12 },
  });
  applyOuterBorder(ws, 2, 6, 5, 8);
  setMergedCell(ws, 6, 6, 6, 8, SII_OFICINA, { font: { size: 8 } });

  let r = 7;
  for (const line of COMPANY_LINES) {
    setMergedCell(ws, r, 1, r, 4, line, {
      font: { bold: r === 7, size: 8 },
      alignment: { horizontal: 'left' },
    });
    ws.getRow(r).height = 12;
    r += 1;
  }

  r = 12;
  setMergedCell(
    ws,
    r,
    1,
    r,
    8,
    `ISSUE DATE: ${p.issueCity || 'Santiago'}, ${invoiceIssueDateText(p.fechaInvoice, fmtDateLongEn)}`,
    { font: { size: 9 }, alignment: { horizontal: 'left' } }
  );
  ws.getRow(r).height = 18;
  r += 2;

  const addrs = addressLines(p.clienteDireccion, p.paisDestino);
  const customerStart = r;
  for (let row = customerStart; row < customerStart + 9; row += 1) {
    ws.getRow(row).height = row >= customerStart + 1 && row <= customerStart + 3 ? 16 : 13;
  }
  setMergedCell(ws, r, 1, r, 2, 'Mr./Mrs.:', { alignment: { horizontal: 'left' } });
  setMergedCell(ws, r, 3, r, 4, upper(p.clienteNombre), { alignment: { horizontal: 'left' } });
  setMergedCell(ws, r + 1, 1, r + 3, 2, 'Address:', {
    alignment: { horizontal: 'left', vertical: 'top' },
  });
  setMergedCell(ws, r + 1, 3, r + 3, 4, addrs.join('\n'), {
    alignment: { horizontal: 'left', vertical: 'top' },
  });
  const customerRows = [
    ['State:', ''],
    ['City/Country:', upper(p.paisDestino)],
    ['Phone:', ''],
    ['Fax:', ''],
    ['Postal Code/Pin Code:', safe(p.pinCode)],
  ];
  customerRows.forEach(([label, value], index) => {
    const row = r + 4 + index;
    setMergedCell(ws, row, 1, row, 2, label, { alignment: { horizontal: 'left' } });
    setMergedCell(ws, row, 3, row, 4, value, { alignment: { horizontal: 'left' } });
  });
  applyOuterBorder(ws, customerStart, 1, customerStart + 8, 4);

  setGridCells(ws, r, [[5, 6], [7, 8]], ['Country of Origin', 'Destination Country'], {
    font: { size: 7 },
  });
  setMergedCell(ws, r + 1, 5, r + 2, 6, upper(p.paisOrigen), { border: BORDER });
  setMergedCell(ws, r + 1, 7, r + 2, 8, upper(p.paisDestino), { border: BORDER });
  setGridCells(ws, r + 3, [[5, 6], [7, 8]], ['Loading Port', 'Unloading Port'], {
    font: { size: 7 },
  });
  setMergedCell(ws, r + 4, 5, r + 5, 6, upper(loadingPortForExcel(p.puertoOrigen)), {
    border: BORDER,
  });
  setMergedCell(ws, r + 4, 7, r + 5, 8, upper(unloadingPortForExcel(p.puertoDestino, p.paisDestino)), {
    border: BORDER,
  });
  setMergedCell(ws, r + 6, 5, r + 6, 8, 'Place of delivery', {
    border: BORDER,
    font: { size: 7 },
  });
  setMergedCell(ws, r + 7, 5, r + 8, 8, upper(p.lugarEntrega || p.puertoDestino), {
    border: BORDER,
  });
  r += 9;

  const vesselRanges = [[1, 2], [3, 3], [4, 6], [7, 8]];
  setGridCells(ws, r, vesselRanges, ['Vessel', 'Contract No.', 'Purchase Order', 'Bill of Lading'], {
    font: { bold: true },
  });
  ws.getRow(r).height = 17;
  r += 1;
  setGridCells(ws, r, vesselRanges, [
    upper(p.nombreNave),
    upper(p.contractNo),
    upper(p.purchaseOrder),
    upper(p.nombreBl),
  ]);
  ws.getRow(r).height = 22;
  r += 1;

  const salesRanges = [[1, 2], [3, 3], [4, 6]];
  setGridCells(ws, r, [...salesRanges, [7, 8]], [
    'Sales Terms',
    'Incoterms',
    'Payment Terms',
    'TOTAL WEIGHT (kg)',
  ], { font: { bold: true } });
  r += 1;
  setMergedCell(ws, r, 1, r + 1, 2, upper(p.salesTerms), { border: BORDER });
  setMergedCell(ws, r, 3, r + 1, 3, upper(p.incoterms), { border: BORDER });
  setMergedCell(ws, r, 4, r + 1, 6, safe(p.paymentTerms), { border: BORDER, font: { size: 7 } });
  setGridCells(ws, r, [[7, 7], [8, 8]], ['Net Weight', 'Gross Weight'], {
    font: { bold: true, size: 7 },
  });
  r += 1;
  setGridCells(ws, r, [[7, 7], [8, 8]], [fmtNumCL(p.netWeight, 3), fmtNumCL(p.grossWeight, 3)]);
  r += 1;

  setMergedCell(ws, r, 1, r, 8, 'Please Remit Payment to PALM TRADE CHILE SPA', {
    border: BORDER,
    font: { bold: true, size: 9 },
  });
  ws.getRow(r).height = 18;
  r += 1;

  const headR1 = r;
  writeDocumentRow(
    ws,
    r,
    [
      'Internal Code',
      'Quantity',
      'MERCHANDISE DESCRIPTION',
      'DJC',
      '',
      '',
      '',
      '',
    ],
    {
      border: true,
      font: { bold: true, size: 7 },
      align: { horizontal: 'center', wrapText: true },
      height: 28,
    }
  );
  r += 1;
  writeDocumentRow(
    ws,
    r,
    ['', '', '', 'N°', 'Date', 'Currency', 'UNIT PRICE', 'TOTAL VALUE'],
    {
      border: true,
      font: { bold: true, size: 7 },
      align: { horizontal: 'center', wrapText: true },
      height: 22,
    }
  );
  ws.mergeCells(headR1, 2, headR1 + 1, 2);
  ws.mergeCells(headR1, 3, headR1 + 1, 3);
  ws.mergeCells(headR1, 4, headR1 + 1, 4);
  ws.mergeCells(headR1, 5, headR1, 9);
  styleRange(ws, headR1, 2, headR1 + 1, 9, {
    border: BORDER,
    font: { bold: true, size: 7 },
    alignment: { horizontal: 'center', vertical: 'middle', wrapText: true },
  });
  ws.pageSetup.printTitlesRow = `${headR1}:${headR1 + 1}`;
  r += 1;

  const textos = textosAlternativosParaPdf(p.textosAlternativosPtc);
  for (const item of textos.filter((t) => t.posicion === 'arriba')) {
    writeDocumentRow(ws, r, ['', '', upper(item.texto), '', '', '', '', ''], {
      wrap: true,
      font: { size: 7 },
      align: { horizontal: 'left', vertical: 'top', wrapText: true },
    });
    styleRange(ws, r, 2, r, 9, { border: VERTICAL_BORDER });
    ws.getRow(r).height = excelRowHeightForWrappedText(item.texto);
    r += 1;
  }

  const textoArriba =
    safe(p.textoMercaderiaPtcArriba).trim() ||
    pairFromTextosAlternativosPtc(p.textosAlternativosPtc).arriba;
  const lineas = stripProductNameIfCoveredByTextoArriba(
    normalizeGeneralMerchandiseDescription(p.lineas || [], p.productDescriptionSnapshot),
    { textoArriba, productDescriptionSnapshot: p.productDescriptionSnapshot }
  );
  if (!lineas.length) {
    writeDocumentRow(ws, r, Array(8).fill(''));
    styleRange(ws, r, 2, r, 9, { border: VERTICAL_BORDER });
    ws.getRow(r).height = 24;
    r += 1;
  } else {
    for (const ln of lineas) {
      writeDocumentRow(
        ws,
        r,
        [
          safe(ln.internalCode),
          safe(ln.quantity),
          safe(ln.description),
          safe(ln.djcNum),
          safe(ln.djcDate),
          safe(ln.currency),
          fmtPriceEs(ln.unitPrice),
          fmtPriceEs(ln.totalValue),
        ],
        { font: { size: 7 }, wrap: true, align: { vertical: 'top', wrapText: true } }
      );
      styleRange(ws, r, 2, r, 9, { border: VERTICAL_BORDER });
      ws.getRow(r).height = excelRowHeightForWrappedText(ln.description);
      r += 1;
    }
  }

  for (const item of textos.filter((t) => t.posicion === 'abajo')) {
    writeDocumentRow(ws, r, ['', '', upper(item.texto), '', '', '', '', ''], {
      wrap: true,
      font: { size: 7 },
      align: { horizontal: 'left', vertical: 'top', wrapText: true },
    });
    styleRange(ws, r, 2, r, 9, { border: VERTICAL_BORDER });
    ws.getRow(r).height = excelRowHeightForWrappedText(item.texto);
    r += 1;
  }

  const totalUsdNum =
    p.valorFinalUsd != null
      ? Number(p.valorFinalUsd)
      : p.valorUsd != null
        ? Number(p.valorUsd)
        : null;
  const merchandiseUsdNum =
    p.valorMercaderiaUsd != null ? Number(p.valorMercaderiaUsd) : totalUsdNum;
  const moneda = safe(p.currency || 'USD').toUpperCase();
  const totalesIzq = totalVolumeSummaryText(
    p.totalBundles,
    p.totalM3,
    p.unidadVolumenPtc,
    (v, d) => fmtNumCL(v, d ?? 2)
  );

  writeDocumentRow(
    ws,
    r,
    [totalesIzq, '', '', '', '', '', `Total ${moneda}`, merchandiseUsdNum != null ? fmtPriceEs(merchandiseUsdNum) : ''],
    { border: true, font: { bold: true, size: 7 } }
  );
  ws.mergeCells(r, 2, r, 7);
  styleRange(ws, r, 2, r, 9, { border: BORDER, font: { bold: true, size: 7 } });
  r += 1;

  const ajustes = Array.isArray(p.ajustesPtc) ? p.ajustesPtc : [];
  for (const ajuste of ajustes) {
    writeDocumentRow(
      ws,
      r,
      [
        safe(ajuste.descripcion),
        '',
        '',
        '',
        '',
        '',
        safe(ajuste.tipo),
        `${ajuste.valor < 0 ? '-' : '+'}${fmtPriceEs(Math.abs(Number(ajuste.valor)))} ${moneda}`,
      ],
      { border: true, font: { size: 7 } }
    );
    ws.mergeCells(r, 2, r, 7);
    styleRange(ws, r, 2, r, 9, { border: BORDER, font: { size: 7 } });
    r += 1;
  }

  const composicion = [
    ['Valor FOB', p.valorFobPtc],
    ['Valor Flete', p.valorFletePtc],
    ['Valor Seguro', p.valorSeguroPtc],
  ].filter(([, value]) => value != null && value !== '' && Number.isFinite(Number(value)));
  if (composicion.length) {
    let composicionTotal = 0;
    for (const [label, value] of composicion) {
      composicionTotal += Number(value);
      writeDocumentRow(ws, r, ['', '', '', '', '', '', label, `${fmtPriceEs(value)} ${moneda}`], {
        border: true,
        font: { size: 7 },
      });
      ws.mergeCells(r, 2, r, 7);
      styleRange(ws, r, 2, r, 9, { border: BORDER, font: { size: 7 } });
      r += 1;
    }
    writeDocumentRow(
      ws,
      r,
      ['Valor total', '', '', '', '', '', '', `${fmtPriceEs(composicionTotal)} ${moneda}`],
      { border: true, font: { bold: true, size: 7 } }
    );
    ws.mergeCells(r, 2, r, 7);
    styleRange(ws, r, 2, r, 9, { border: BORDER, font: { bold: true, size: 7 } });
    r += 1;
  }

  if (totalUsdNum != null || ajustes.length || composicion.length) {
    writeDocumentRow(
      ws,
      r,
      [
        totalInvoiceLabel(moneda),
        '',
        '',
        '',
        '',
        '',
        '',
        totalUsdNum != null ? `${fmtPriceEs(totalUsdNum)} ${moneda}` : '',
      ],
      { border: true, font: { bold: true, size: 7 } }
    );
    ws.mergeCells(r, 2, r, 7);
    styleRange(ws, r, 2, r, 9, { border: BORDER, font: { bold: true, size: 7 } });
    r += 1;
  }

  const dueWords = totalUsdNum != null ? totalDueWordsPtc(totalUsdNum, moneda) : '';
  setMergedCell(ws, r, 1, r, 8, {
    richText: [
      { font: { name: 'Arial', bold: true, size: 8 }, text: `TOTAL DUE: ${dueWords}\n` },
      { font: { name: 'Arial', size: 7 }, text: '( IN LETTERS )' },
    ],
  }, {
    border: BORDER,
    alignment: { horizontal: 'left', vertical: 'top', wrapText: true },
  });
  ws.getRow(r).height = 48;
  r += 2;
  const signatureId = wb.addImage({ base64: signatureBase64, extension: 'png' });
  ws.addImage(signatureId, {
    tl: { col: 6.35, row: r - 1 },
    ext: { width: 150, height: 56 },
    editAs: 'oneCell',
  });
  r += 4;
  setMergedCell(ws, r, 6, r, 8, '', { border: { bottom: BORDER.bottom } });
  r += 1;
  setMergedCell(ws, r, 6, r, 8, 'SIGNATURE', { font: { size: 7 } });
  r += 1;
  setMergedCell(ws, r, 6, r, 8, 'p.p. PALM TRADE CHILE SPA', { font: { size: 8 } });

  styleRange(ws, 1, 1, r, 9, {
    fill: WHITE_FILL,
    font: { name: 'Arial', color: { argb: 'FF000000' } },
  });
  ws.pageSetup.printArea = `A1:I${r}`;

  appendBankSheet(wb, logoBase64);
  return wb;
}

export async function generateInvoicePtcExcel(params) {
  const wb = await buildInvoicePtcExcelWorkbook(params);
  await workbookToDownload(wb, invoicePtcExcelFileName(params));
}

export { invoicePtcExcelFileName };
